/* oxlint-disable typescript/no-unsafe-argument, typescript/no-unsafe-member-access */
import { service, type Registry as Services } from '@ember/service';

import * as Sentry from '@sentry/ember';
import { variation } from 'ember-launch-darkly';
import BaseSessionService from 'ember-simple-auth/services/session';
import window from 'ember-window-mock';

import { ErrorInfo } from 'register-partners-js/utils/error-info';
import { getRedirectionToken } from 'register-partners-js/utils/get-redirection-token';

export default class SessionService extends BaseSessionService {
  @service declare currentUser: Services['current-user'];
  // @ts-expect-error -- this service is not typed yet
  @service store;
  // @ts-expect-error -- this service is not typed yet
  @service router;

  async handleAuthentication(routeAfterAuthentication: string): Promise<void> {
    try {
      await this.currentUser.load();
      await this.attachUserToRegisterOrganization();
      // If a language switch happened before the session started, set the new locale in the user profile
      let user = this.currentUser.user;
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      let loggedOutLocaleCode = sessionStorage?.getItem('logged-out-locale-code');
      if (user && loggedOutLocaleCode) {
        user.set('locale', loggedOutLocaleCode);
        await user.save();
        // oxlint-disable-next-line typescript/no-unnecessary-condition
        sessionStorage?.removeItem('logged-out-locale-code');
      }
    } catch {
      await this.invalidate();
    }
    /**
     * Redirect to the specified routeAfterAuthentication in the ember-simple-auth config after successful authentication
     */
    super.handleAuthentication(routeAfterAuthentication);
  }

  // The register API only attaches the new user to the organization when the partner
  // registration is fetched. Doing it here covers every authentication path, because
  // register-js is the next stop and nothing else in this app fetches it first.
  async attachUserToRegisterOrganization(): Promise<void> {
    if (!variation('feature--partner-migration-enabled')) return;

    // oxlint-disable-next-line typescript/no-unsafe-assignment
    let registrationId = this.store.peekAll('register-redirection-token')[0]?.registrationID;
    if (!registrationId) return;

    try {
      await this.store.findRecord('partner-registration', registrationId, { reload: true });
    } catch (error) {
      if (ErrorInfo.for(error).shouldSendToSentry) {
        Sentry.captureException(error);
      }
    }
  }

  get logoutUrl(): string {
    let token = getRedirectionToken(this.store);

    if (token) {
      // oxlint-disable-next-line typescript/no-unsafe-return
      return this.router.urlFor('welcome', token);
    }
    return '/';
  }

  handleInvalidation(): void {
    this.currentUser.user = null;
    Sentry.getCurrentScope().setUser(null);
    Sentry.getCurrentScope().setTag('organization_id', null);

    window.location.replace(this.logoutUrl);
  }
}

declare module '@ember/service' {
  interface Registry {
    session: SessionService;
  }
}
